import { CountryFlag, countryName } from "@nexus/ui";
import {
  firstError,
  MetricStat,
  unwrap,
  Widget,
  WidgetSkeleton,
  WidgetState,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import {
  IP_BLOCKING_SERIES_CONTRACT,
  RankingList,
  RANKING_ROW,
  RANKING_METRIC,
  RANKING_VIZ,
} from "@nexus/widget-kit";
import type { IpBlockingSeriesData } from "@nexus/widget-kit";

export interface IpBlockingIspRankingConfig {
  title?: string;
}
export const manifest: WidgetManifest = {
  id: "isp-blocking-rate",
  name: "ISP Blocking Rate",
  version: "1.0.0",
  description:
    "Targets blocked by at least half of eligible ISPs, with the bottom five ISP rates and countries.",
  dataRequests: {
    series: {
      source: "ip-blocking-effectiveness",
      contract: IP_BLOCKING_SERIES_CONTRACT,
      configurable: true,
      sources: ["ip-blocking-effectiveness", "domain-blocking-effectiveness"],
    },
  },
};
export const defaultConfig: IpBlockingIspRankingConfig = { title: "ISP Blocking Rate" };

export function Component({ data, config }: WidgetProps<IpBlockingIspRankingConfig>) {
  const report = unwrap<IpBlockingSeriesData>(data.series);
  const error = firstError(data, ["series"]);
  const effectiveness = report?.effectiveness;
  const count = new Intl.NumberFormat("en");
  const percent = new Intl.NumberFormat("en", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return (
    <Widget title={config.title?.trim() || defaultConfig.title || manifest.name} intrinsicHeight>
      {data.series?.isLoading && !report ? (
        <WidgetSkeleton shape="list" />
      ) : error ? (
        <WidgetIssueState error={error} />
      ) : !effectiveness ? (
        <WidgetState title="ISP data unavailable" />
      ) : !effectiveness.isps.length ? (
        <WidgetState title="No eligible ISP sendings" />
      ) : (
        <div className="min-w-0 space-y-3">
          <dl className="density-metric-padding">
            <MetricStat
              label="Blocked by at least half of ISPs"
              value={
                effectiveness.majorityRatio == null
                  ? "Unavailable"
                  : percent.format(effectiveness.majorityRatio)
              }
              detail={
                effectiveness.eligibleTargets != null && effectiveness.blockedTargets != null
                  ? `${count.format(effectiveness.blockedTargets)} / ${count.format(effectiveness.eligibleTargets)} distinct targets`
                  : undefined
              }
            />
          </dl>
          <section className="min-w-0">
            <h3 className="px-3 text-xs font-semibold text-secondary">Bottom 5 ISPs</h3>
            <RankingList identity="ISP" metric="Blocked" visualization="Sendings">
              {[...effectiveness.isps]
                .filter((item) => item.ratio != null)
                .sort(
                  (first, second) =>
                    (first.ratio ?? 0) - (second.ratio ?? 0) || first.isp.localeCompare(second.isp),
                )
                .slice(0, 5)
                .map((item) => {
                  const country = countryName(item.country) ?? "Country unavailable";
                  return (
                    <div key={item.isp} className={RANKING_ROW}>
                      <div className="min-w-0 flex-1">
                        <span className="block truncate text-sm" title={item.isp}>
                          {item.isp}
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-secondary">
                          <CountryFlag country={item.country} placeholder={false} />
                          <span className="truncate">{country}</span>
                        </span>
                      </div>
                      <span className={`${RANKING_METRIC} text-sm tabular-nums`}>
                        {item.ratio == null ? "Unavailable" : percent.format(item.ratio)}
                      </span>
                      <div
                        className={`${RANKING_VIZ} flex flex-col justify-center gap-1`}
                        title={`${count.format(item.blockedSendings)} blocked / ${count.format(item.eligibleSendings)} eligible approved ticket/ISP sendings`}
                      >
                        <span className="text-right text-2xs tabular-nums">
                          {count.format(item.blockedSendings)}/{count.format(item.eligibleSendings)}
                        </span>
                        <div className="h-1 bg-border">
                          <div
                            className={`h-full ${item.ratio != null && item.ratio >= 0.7 ? "bg-status-ok" : item.ratio != null && item.ratio >= 0.4 ? "bg-status-warn" : "bg-status-error"}`}
                            style={{ width: `${(item.ratio ?? 0) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
            </RankingList>
          </section>
        </div>
      )}
    </Widget>
  );
}
const widgetModule: WidgetModule<IpBlockingIspRankingConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
